import PocketBase, { LocalAuthStore } from "pocketbase"

import { env } from "@/lib/env"
import { getIdToken } from "@/lib/firebase"

/**
 * A customer's session on one shop's server.
 *
 * The chain is the staff one — Firebase, MainApp, a five-minute shard token,
 * a session on the shop's server — with one extra link at the front: a shop is
 * only entered from its own booking page, so signing in there is also what
 * makes the customer a member of it.
 *
 * The clients are separate from the dashboard's on purpose. They are the same
 * two servers, but a member of staff reading another shop's booking page in the
 * same browser would otherwise sign their dashboard into that shop's server
 * without ever leaving the page they were on. Firebase is deliberately shared:
 * it is one account either way.
 */

const customerMainPb = new PocketBase(
  env.mainAppUrl,
  new LocalAuthStore("manito.pb.customer.main"),
)

const customerTenantPb = new PocketBase(
  env.mainAppUrl,
  new LocalAuthStore("manito.pb.customer.tenant"),
)

customerMainPb.autoCancellation(false)
customerTenantPb.autoCancellation(false)

// Tells the audit log that the customer made this themselves, rather than the
// front desk making it for them.
customerTenantPb.beforeSend = (url, options) => {
  options.headers = { ...options.headers, "X-Client": "booking" }
  return { url, options }
}

/** Nobody is signed in with Firebase, so none of the rest can be attempted. */
export class NotSignedInError extends Error {
  constructor() {
    super("Not signed in")
    this.name = "NotSignedInError"
  }
}

/** What the shop's server knows about the signed-in customer. */
export interface CustomerRecord {
  id: string
  name: string
  email: string
  tenant_id: string
  capacity: string
  main_user: string
}

export interface CustomerProfile {
  name: string
  phone: string
  email: string
  /** Calling code and country, e.g. "+357" and "Cyprus". */
  country_code?: string
  country?: string
  address?: string
  city?: string
  postal_code?: string
  imageUrl?: string
  /**
   * The fields the shop keeps its own record of, by the names above. Shown,
   * never changed from the customer's side: the shop's row wins (ADR
   * customer accounts Q4, MANIT-1153). The server ignores them on save.
   */
  held?: string[]
}

interface CustomerProfileResponse {
  name: string
  phone: string
  email: string
  country_code?: string
  country?: string
  address?: string
  city?: string
  postal_code?: string
  id: string
  image: string
  held?: string[]
}

export function isCustomerProfileComplete(profile: CustomerProfile | null): boolean {
  return Boolean(profile?.name.trim() && profile.phone.trim())
}

interface AuthResponse {
  token: string
  record: Record<string, unknown>
}

interface ShardTokenResponse {
  token: string
  serverUrl: string
  tenantId: string
}

/** The customer's record on this shop's server, or null if signed out. */
export function currentCustomer(tenantId: string): CustomerRecord | null {
  const record = customerTenantPb.authStore.record as CustomerRecord | null

  if (!customerTenantPb.authStore.isValid) return null
  if (record?.tenant_id !== tenantId) return null

  return record
}

/**
 * Opens a customer session on this shop's server, joining the shop if this is
 * the first time.
 *
 * Cheap when there is already a session for this shop, because that is the
 * common case: every page of the funnel asks.
 */
export async function ensureCustomerSession(
  tenantId: string,
  serverUrl: string,
): Promise<CustomerRecord> {
  const existing = currentCustomer(tenantId)
  if (existing) {
    customerTenantPb.baseURL = serverUrl
    return existing
  }

  const idToken = await getIdToken()
  if (!idToken) throw new NotSignedInError()

  const main = await customerMainPb.send<AuthResponse>("/api/firebase-auth", {
    method: "POST",
    body: { idToken },
  })
  customerMainPb.authStore.save(main.token, main.record as never)

  // Booking is what being a customer means, so this needs no approval from the
  // shop and is safe to repeat: it answers the same on the hundredth visit.
  await customerMainPb.send("/api/customer-membership", {
    method: "POST",
    body: { tenantId },
  })

  const shard = await customerMainPb.send<ShardTokenResponse>("/api/shard-token", {
    method: "POST",
    body: { tenantId },
  })

  // The directory named the server, but the token is the authority on where it
  // may be spent.
  customerTenantPb.baseURL = shard.serverUrl

  const session = await customerTenantPb.send<AuthResponse>("/api/session", {
    method: "POST",
    body: { token: shard.token },
  })
  customerTenantPb.authStore.save(session.token, session.record as never)

  return session.record as unknown as CustomerRecord
}

/**
 * The client to read this shop's collections with, or null when signed out.
 *
 * Handed out rather than exported so nothing can query a shop the session is
 * not for: the rules would refuse it, but a request that cannot be made is
 * better than one that comes back empty and looks like "you have nothing".
 */
export function customerPb(tenantId: string, serverUrl: string): PocketBase | null {
  if (!currentCustomer(tenantId)) return null

  // LocalAuthStore restores the record and token, not the shard URL. Set it
  // before handing the client out or a cold page load sends tenant collection
  // requests to MainApp, where those collections correctly return 404.
  customerTenantPb.baseURL = serverUrl
  return customerTenantPb
}

/** Contact details already held by this shop for the signed-in customer. */
export async function customerProfile(
  tenantId: string,
  serverUrl: string,
): Promise<CustomerProfile | null> {
  const pb = customerPb(tenantId, serverUrl)
  if (!pb) return null

  const response = await pb.send<CustomerProfileResponse>(
    `/api/public/profile?tenant=${encodeURIComponent(tenantId)}`,
    { method: "GET" },
  )
  return customerProfileFromResponse(pb, response)
}

/** Saves contact details on the customer record owned by this account. */
export async function saveCustomerProfile(
  tenantId: string,
  serverUrl: string,
  profile: CustomerProfile,
): Promise<CustomerProfile> {
  const pb = customerPb(tenantId, serverUrl)
  if (!pb) throw new NotSignedInError()

  const response = await pb.send<CustomerProfileResponse>(
    `/api/public/profile?tenant=${encodeURIComponent(tenantId)}`,
    {
      method: "PUT",
      body: {
        name: profile.name,
        phone: profile.phone,
        email: profile.email,
        country_code: profile.country_code,
        country: profile.country,
        address: profile.address,
        city: profile.city,
        postal_code: profile.postal_code,
      },
    },
  )
  return customerProfileFromResponse(pb, response)
}

export async function saveCustomerProfileImage(
  tenantId: string,
  serverUrl: string,
  image: File,
): Promise<CustomerProfile> {
  const pb = customerPb(tenantId, serverUrl)
  if (!pb) throw new NotSignedInError()
  const body = new FormData()
  body.append("image", image)
  const response = await pb.send<CustomerProfileResponse>(
    `/api/public/profile/image?tenant=${encodeURIComponent(tenantId)}`,
    { method: "POST", body },
  )
  return customerProfileFromResponse(pb, response)
}

export async function removeCustomerProfileImage(
  tenantId: string,
  serverUrl: string,
): Promise<CustomerProfile> {
  const pb = customerPb(tenantId, serverUrl)
  if (!pb) throw new NotSignedInError()
  const response = await pb.send<CustomerProfileResponse>(
    `/api/public/profile/image?tenant=${encodeURIComponent(tenantId)}`,
    { method: "DELETE" },
  )
  return customerProfileFromResponse(pb, response)
}

async function customerProfileFromResponse(
  pb: PocketBase,
  response: CustomerProfileResponse,
): Promise<CustomerProfile> {
  let imageUrl = ""
  if (response.id && response.image) {
    const token = await pb.files.getToken()
    imageUrl = pb.files.getURL(
      { id: response.id, collectionName: "customers" },
      response.image,
      { token },
    )
  }
  return {
    name: response.name,
    phone: response.phone,
    country_code: response.country_code,
    country: response.country,
    address: response.address,
    city: response.city,
    postal_code: response.postal_code,
    email: response.email,
    imageUrl,
    held: response.held ?? [],
  }
}

/**
 * The token to send with a booking, or undefined when signed out.
 *
 * The public routes work without it. Sending it is what makes the booking land
 * on the customer's own record instead of a fresh one, and the server reads
 * whose it is from the token rather than from the form.
 */
export function customerToken(tenantId: string): string | undefined {
  if (!currentCustomer(tenantId)) return undefined

  return customerTenantPb.authStore.token
}

/**
 * Tells a component when the session on this shop's server appears or goes.
 *
 * Firebase's own listener is not enough on its own: signing in leaves a gap
 * while the membership and the shard token are fetched, and a form that
 * unlocked on the Firebase event would send its booking anonymously in that
 * gap and be refused by a shop that requires an account.
 */
export function onCustomerSession(listener: () => void): () => void {
  return customerTenantPb.authStore.onChange(listener)
}

/** Forgets both sessions. Firebase sign-out is the caller's business. */
export function clearCustomerSessions(): void {
  customerMainPb.authStore.clear()
  customerTenantPb.authStore.clear()
}
